import type { ComponentChildren, JSX, Ref } from 'preact';
import { useId } from 'preact/hooks';
import { cn } from '@/lib/cn';

export type TextareaState = 'default' | 'success' | 'error';

export interface TextareaProps extends JSX.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  state?: TextareaState;
  autoResize?: boolean;
  textareaRef?: Ref<HTMLTextAreaElement>;
  label?: ComponentChildren;
  hint?: ComponentChildren;
}

const BASE =
  'block min-w-0 w-full rounded-md border bg-surface px-3 py-2 text-13 text-fg placeholder:text-fg-faint outline-none transition-[border-color,box-shadow,background,transform] duration-med ease-snappy focus-visible:scale-[1.002] disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-surface-2';

const STATE: Record<TextareaState, string> = {
  default:
    'border-border hover:border-border-strong focus-visible:border-brand focus-visible:shadow-focus',
  success:
    'border-ok hover:border-ok focus-visible:border-ok focus-visible:shadow-focus-ok',
  error:
    'border-error hover:border-error focus-visible:border-error focus-visible:shadow-focus-error',
};

export function Textarea({
  invalid,
  state,
  autoResize,
  className,
  rows = 4,
  onInput,
  textareaRef,
  label,
  hint,
  ...rest
}: TextareaProps) {
  const resolvedState = invalid ? 'error' : state ?? 'default';
  const st = STATE[resolvedState];

  const generatedId = useId();
  const textareaId = (rest.id as string | undefined) ?? generatedId;
  const hintId = hint ? `${textareaId}-hint` : undefined;

  const handleInput = (event: JSX.TargetedEvent<HTMLTextAreaElement>) => {
    if (autoResize) {
      const el = event.currentTarget;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
    onInput?.(event as JSX.TargetedInputEvent<HTMLTextAreaElement>);
  };

  const field = (
    <textarea
      ref={textareaRef}
      rows={rows}
      aria-invalid={resolvedState === 'error'}
      aria-describedby={hintId}
      onInput={handleInput}
      className={cn(
        BASE,
        st,
        autoResize && 'resize-none overflow-hidden',
        className,
      )}
      {...rest}
      id={textareaId}
    />
  );

  // Sin label/hint mantenemos el comportamiento histórico (textarea pelado)
  // para no alterar layouts que ya envuelven el primitivo por su cuenta.
  if (!label && !hint) return field;

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={textareaId} className="block text-13 font-semibold text-fg-strong mb-2">
          {label}
        </label>
      ) : null}
      {field}
      {hint ? (
        <p
          id={hintId}
          className={cn(
            'text-xs mt-1.5',
            resolvedState === 'error' ? 'text-error' :
            resolvedState === 'success' ? 'text-ok' :
            'text-fg-muted',
          )}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
